import React from 'react'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import { fetchOperationalDashboard } from '../../services/informationSystemPhase15Api'
import { fetchOperationalDashboardV2 } from '../../services/informationSystemPhase16Api'
import styles from '../../styles/components/assets/AssetsRiskCompliance.module.scss'

export default function OperationalDashboardPanel() {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()
  const [data, setData] = React.useState<Record<string, unknown> | null>(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    if (!token) return
    fetchOperationalDashboardV2(token, orgId)
      .then(setData)
      .catch(() =>
        fetchOperationalDashboard(token, orgId)
          .then(setData)
          .catch(() => setData(null))
      )
      .finally(() => setLoading(false))
  }, [token, orgId])

  if (loading) return <p className={styles.loadingText}>{t('common.loading')}</p>
  if (!data) return null

  const topExposed = (data.topExposedSystems as Array<Record<string, unknown>>) || []
  const critical = (data.systemsWithCriticalVulnerabilities as Array<Record<string, unknown>>) || []
  const crossBorder = (data.crossBorderDataFlows as Array<Record<string, unknown>>) || []
  const slaBreaches = (data.slaBreaches as Array<Record<string, unknown>>) || []
  const staleEvidence = (data.systemsWithStaleEvidence as Array<Record<string, unknown>>) || []
  const blockers = (data.systemsWithCriticalBlockers as Array<Record<string, unknown>>) || []
  const blast = (data.highestBlastRadiusVulnerabilities as Array<Record<string, unknown>>) || []

  return (
    <div className={`${styles.intelligenceCard} ${styles.intelligenceCardWide}`}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{t('assets.intelligence.dashboardTitle', 'Operational overview')}</h3>
      </div>
      <div className={styles.cardBody}>
        <div className={styles.widgetGrid}>
          <WidgetBlock
            title={t('assets.intelligence.widgetTopExposed', 'Top exposed systems')}
            items={topExposed.map((s) => String(s.name))}
            empty={t('assets.intelligence.none', 'None')}
          />
          <WidgetBlock
            title={t('assets.intelligence.widgetCriticalVulns', 'Critical vulnerabilities')}
            items={critical.map((s) => String(s.name))}
            empty={t('assets.intelligence.none', 'None')}
          />
          <WidgetBlock
            title={t('assets.intelligence.widgetCrossBorder', 'Cross-border flows')}
            items={crossBorder.map((f) => `${f.source} → ${f.target}`)}
            empty={t('assets.intelligence.none', 'None')}
          />
          <WidgetBlock
            title={t('assets.workflows.widgetSlaBreaches', 'SLA breaches')}
            items={slaBreaches.map((s) => `${s.name} (${s.breachCount})`)}
            empty={t('assets.intelligence.none', 'None')}
          />
          <WidgetBlock
            title={t('assets.workflows.widgetStaleEvidence', 'Stale evidence')}
            items={staleEvidence.map((s) => String(s.name))}
            empty={t('assets.intelligence.none', 'None')}
          />
          <WidgetBlock
            title={t('assets.workflows.widgetCriticalBlockers', 'Critical blockers')}
            items={blockers.map((s) => String(s.name))}
            empty={t('assets.intelligence.none', 'None')}
          />
          <WidgetBlock
            title={t('assets.workflows.widgetBlastRadius', 'Highest blast radius')}
            items={blast.map((b) => String(b.impactSummary || b.cve || b.title || ''))}
            empty={t('assets.intelligence.none', 'None')}
          />
        </div>
      </div>
    </div>
  )
}

function WidgetBlock({ title, items, empty }: { title: string; items: string[]; empty: string }) {
  return (
    <div className={styles.widgetBlock}>
      <p className={styles.widgetBlockTitle}>{title}</p>
      {items.length === 0 ? (
        <p className={styles.widgetEmpty}>{empty}</p>
      ) : (
        <ul className={styles.widgetBlockList}>
          {items.slice(0, 5).map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  )
}
